import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import firebaseService from '../firebase.service';
import { buildMediaIndex } from '../utils/fileUtils';
import { useDataCache } from '../contexts/DataCacheContext';
import { useProject } from '../contexts/ProjectContext';
import { db } from '../firebase';
import { doc, setDoc, deleteDoc, getDocs, collection } from 'firebase/firestore';
import { processExcelToIndividualRecords } from '../utils/excelProcessor';
import { convertToWebP, batchConvertToWebP } from '../utils/imageConverter';
import { updateMediaDetectionForBooks } from '../utils/mediaDetectionUpdater';
import AuthComponent from './AuthComponent';

const AdminPanel = () => {
  const { projects: contextProjects, defaultProject, activeProject: contextActiveProject, projectsLoaded } = useProject();
  const [activeProject, setActiveProject] = useState(contextActiveProject);
  const hasSyncedDefault = useRef(false);
  const [uploadType, setUploadType] = useState('bookcovers');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, _setUploadStatus] = useState('');
  const uploadStatusRef = useRef('');
  const setUploadStatus = (val) => { uploadStatusRef.current = val; _setUploadStatus(val); };
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectId, setNewProjectId] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectStatus, setProjectStatus] = useState('');
  const [managedProjects, setManagedProjects] = useState(contextProjects);
  
  const bookCoverInputRef = useRef(null);
  const glbModelInputRef = useRef(null);
  const glbRawInputRef = useRef(null);
  const dataCache = useDataCache();
  const bookListInputRef = useRef(null);
  const companyListInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const { t } = useTranslation();

  // Sync managed projects when context updates from Firestore
  useEffect(() => {
    setManagedProjects(contextProjects);
  }, [contextProjects]);

  // Sync local activeProject with Firestore default once loaded
  useEffect(() => {
    if (projectsLoaded && !hasSyncedDefault.current) {
      setActiveProject(defaultProject);
      hasSyncedDefault.current = true;
    }
  }, [projectsLoaded, defaultProject]);

  // Handle authentication state changes
  const handleAuthChange = (user) => {
    setIsAuthenticated(!!user);
    if (user) {
      console.log('✅ AdminPanel: User authenticated:', user.email);
    } else {
      console.log('❌ AdminPanel: User not authenticated');
    }
  };

  // Process Excel file and detect media types
  const processExcelWithMediaTypes = async (file, project) => {
    try {
      console.log(`🔍 Processing Excel file for media type detection: ${file.name}`);
      
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        throw new Error('Excel file appears to be empty or has no data rows');
      }
      
      const headers = jsonData[0];
      const processedData = [headers];
      
      let codeColumnIndex = headers.findIndex(header => 
        header && header.toLowerCase().includes('code')
      );
      
      if (codeColumnIndex === -1) {
        codeColumnIndex = headers.findIndex(header => 
          header && (
            header.toLowerCase().includes('book_code') ||
            header.toLowerCase().includes('bookcode') ||
            header.toLowerCase().includes('id') ||
            header.toLowerCase().includes('identifier')
          )
        );
      }
      
      console.log(`📊 Found code column at index: ${codeColumnIndex}`);
      
      if (!headers.includes('mediaType')) {
        headers.push('mediaType');
      }
      
      // Build media index once (2 API calls) instead of per-book checks
      setUploadStatus('Building media index...');
      const { lookup: mediaLookup } = await buildMediaIndex(project);
      
      let processedCount = 0;
      
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (row && row.length > 0) {
          const newRow = [...row];
          const bookCode = codeColumnIndex !== -1 ? row[codeColumnIndex] : null;
          
          if (bookCode && bookCode.toString().trim()) {
            newRow.push(mediaLookup(bookCode.toString().trim()));
          } else {
            newRow.push('none');
          }
          
          processedData.push(newRow);
          processedCount++;
        }
      }
      
      console.log(`✅ Media type detection complete for ${processedCount} books`);
      
      const newWorkbook = XLSX.utils.book_new();
      const newWorksheet = XLSX.utils.aoa_to_sheet(processedData);
      XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, 'Sheet1');
      
      const excelBuffer = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
      const processedFile = new File([excelBuffer], file.name, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      return processedFile;
    } catch (error) {
      console.error('Error processing Excel file:', error);
      throw error;
    }
  };

  const projects = managedProjects;

  const reloadProjects = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'projects'));
      const loaded = [];
      querySnapshot.forEach((docSnap) => {
        loaded.push({ id: docSnap.id, ...docSnap.data() });
      });
      if (loaded.length > 0) {
        const sorted = loaded
          .filter(p => p.isActive !== false)
          .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
        setManagedProjects(sorted.length > 0 ? sorted : loaded);
      }
    } catch (error) {
      console.error('Failed to reload projects:', error);
    }
  };

  const handleCreateProject = async () => {
    const id = newProjectId.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!id || !newProjectName.trim()) {
      setProjectStatus('Project ID and Name are required.');
      return;
    }
    if (projects.some(p => p.id === id)) {
      setProjectStatus(`Project "${id}" already exists.`);
      return;
    }

    setProjectSaving(true);
    setProjectStatus('');
    try {
      const projectData = {
        id,
        name: newProjectName.trim(),
        description: newProjectDesc.trim(),
        isDefault: false,
        isActive: true,
        order: projects.length,
        createdAt: new Date().toISOString()
      };
      await setDoc(doc(db, 'projects', id), projectData);
      setProjectStatus(`Project "${projectData.name}" created successfully. Reload the page to see it in other views.`);
      setNewProjectId('');
      setNewProjectName('');
      setNewProjectDesc('');
      setShowCreateProject(false);
      await reloadProjects();
    } catch (error) {
      console.error('Error creating project:', error);
      setProjectStatus(`Failed to create project: ${error.message}`);
    } finally {
      setProjectSaving(false);
    }
  };

  const handleSetDefault = async (projectId) => {
    setProjectSaving(true);
    try {
      for (const p of projects) {
        await setDoc(doc(db, 'projects', p.id), {
          id: p.id,
          name: p.name,
          description: p.description || '',
          isDefault: p.id === projectId,
          isActive: p.isActive !== false,
          order: p.order ?? 99,
          createdAt: p.createdAt || new Date().toISOString()
        }, { merge: true });
      }
      setProjectStatus(`"${projects.find(p => p.id === projectId)?.name}" is now the default project.`);
      await reloadProjects();
    } catch (error) {
      console.error('Error setting default project:', error);
      setProjectStatus(`Failed to update: ${error.message}`);
    } finally {
      setProjectSaving(false);
    }
  };

  const handleToggleActive = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    setProjectSaving(true);
    try {
      await setDoc(doc(db, 'projects', projectId), {
        id: project.id,
        name: project.name,
        description: project.description || '',
        isDefault: project.isDefault || false,
        isActive: !project.isActive,
        order: project.order ?? 99,
        createdAt: project.createdAt || new Date().toISOString()
      }, { merge: true });
      setProjectStatus(`"${project.name}" is now ${project.isActive ? 'inactive' : 'active'}.`);
      await reloadProjects();
    } catch (error) {
      console.error('Error toggling project:', error);
      setProjectStatus(`Failed to update: ${error.message}`);
    } finally {
      setProjectSaving(false);
    }
  };

  const uploadTypes = [
    { id: 'bookcovers', name: 'Book Covers (PNG → WebP)', description: 'Upload PNG images, convert to WebP' },
    { id: 'glbmodels', name: '3D Models (GLB)', description: 'Upload GLB 3D model files' },
    { id: 'booklist', name: 'Book List (Excel)', description: 'Upload Excel file with book data' },
    { id: 'companylist', name: 'Company List (Excel)', description: 'Upload Excel file with company data' },
    { id: 'projectlogo', name: 'Fair Logo (PNG/JPG)', description: 'Upload logo image for this book fair (converted to WebP)' }
  ];

  // Optimized booklist processing
  const handleOptimizedBooklistUpload = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStatus('Processing Excel file for individual book records...');
      
      // Process Excel to individual records (also cleans up orphaned books)
      const result = await processExcelToIndividualRecords(
        file, 
        activeProject, 
        (current, total) => {
          const progress = Math.round((current / total) * 100);
          setUploadProgress(progress);
          setUploadStatus(`Processing books... ${current}/${total} (${progress}%)`);
        }
      );
      
      let statusMsg = `✅ Successfully processed ${result.processedCount} individual book records!`;
      if (result.removedCodes && result.removedCodes.length > 0) {
        statusMsg += ` Removed ${result.removedCodes.length} outdated book(s): ${result.removedCodes.join(', ')}`;
      }
      setUploadStatus(statusMsg);
      
      // Clear cache to force reload
      dataCache.clearCache(activeProject, 'books_list');
      dataCache.clearCache(activeProject, 'books_detail');
      
      console.log(`🗑️ Cleared cache for ${activeProject} books`);
      
    } catch (error) {
      console.error('❌ Error processing booklist:', error);
      setUploadStatus(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (files, type, options = {}) => {
    if (!files || files.length === 0) return;

    // Check authentication first
    if (!isAuthenticated) {
      setUploadStatus('❌ Please sign in first to upload files.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Checking authentication...');

    try {
      const formData = new FormData();
      formData.append('project', activeProject);
      formData.append('type', type);
      
      // Process files before uploading
      const processedFiles = [];
      for (const file of Array.from(files)) {
        if (type === 'booklist' && (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls'))) {
          // Process Excel file with media type detection
          setUploadStatus('Processing Excel file and detecting media types...');
          const processedFile = await processExcelWithMediaTypes(file, activeProject);
          processedFiles.push(processedFile);
        } else {
          // Use original file for other types
          processedFiles.push(file);
        }
      }
      
      // Add processed files to FormData
      processedFiles.forEach((file, index) => {
        formData.append(`files`, file);
      });

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Actually upload the processed files to Firebase Storage
      setUploadStatus('Uploading processed files to Firebase Storage...');
      
      for (let i = 0; i < processedFiles.length; i++) {
        const file = processedFiles[i];
        const projectFolder = activeProject.toUpperCase();
        
        let uploadPath = '';
        if (type === 'booklist') {
          uploadPath = `${projectFolder}/data/booklist.xlsx`;
        } else if (type === 'companylist') {
          uploadPath = `${projectFolder}/data/company.xlsx`;
        } else {
          // For other file types, use the original logic
          const fileName = file.name;
          if (type === 'bookcovers') {
            uploadPath = `${projectFolder}/covers/${fileName}`;
          } else if (type === 'glbmodels') {
            uploadPath = `${projectFolder}/models/${fileName}`;
          }
        }
        
        if (uploadPath) {
          try {
            const uploadMetadata = options.skipCompression
              ? { customMetadata: { dracoCompressed: 'true' } }
              : {};
            await firebaseService.uploadFile(file, uploadPath, uploadMetadata);
            console.log(`✅ Uploaded ${file.name} to ${uploadPath}`);
          } catch (error) {
            console.error(`❌ Failed to upload ${file.name}:`, error);
            throw error;
          }
        }
        
        // Update progress
        const progress = 90 + ((i + 1) / processedFiles.length) * 10;
        setUploadProgress(progress);
      }
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Handle different upload types
      switch (type) {
        case 'bookcovers': {
          const coverCodes = processedFiles.map(f => f.name.replace(/\.(webp|png|jpg|jpeg)$/i, ''));
          setUploadStatus(`Uploaded ${files.length} cover(s). Updating media detection...`);
          try {
            await updateMediaDetectionForBooks(activeProject, coverCodes, (current, total) => {
              setUploadStatus(`Updating media detection... ${current}/${total}`);
            });
            setUploadStatus(`✅ Uploaded ${files.length} cover(s) and updated media detection!`);
          } catch (err) {
            console.error('Media detection update failed:', err);
            setUploadStatus(`✅ Uploaded ${files.length} cover(s). Media detection update failed — re-upload the book list to refresh.`);
          }
          dataCache.clearCache(activeProject, 'books');
          dataCache.clearCache(activeProject, 'books_list');
          dataCache.clearCache(activeProject, 'books_detail');
          break;
        }
        case 'glbmodels': {
          const modelCodes = processedFiles.map(f => f.name.replace(/\.glb$/i, ''));
          setUploadStatus(`Uploaded ${files.length} model(s). Updating media detection...`);
          try {
            await updateMediaDetectionForBooks(activeProject, modelCodes, (current, total) => {
              setUploadStatus(`Updating media detection... ${current}/${total}`);
            });
            setUploadStatus(`✅ Uploaded ${files.length} model(s) and updated media detection!`);
          } catch (err) {
            console.error('Media detection update failed:', err);
            setUploadStatus(`✅ Uploaded ${files.length} model(s). Media detection update failed — re-upload the book list to refresh.`);
          }
          dataCache.clearCache(activeProject, 'books');
          dataCache.clearCache(activeProject, 'books_list');
          dataCache.clearCache(activeProject, 'books_detail');
          break;
        }
        case 'booklist':
          // Use optimized processing for booklist
          if (files.length === 1) {
            await handleOptimizedBooklistUpload(files[0]);
          } else {
            setUploadStatus(`✅ Book list updated for ${activeProject}!`);
            // Clear cache to force reload of book data with new media types
            dataCache.clearCache(activeProject, 'books_list');
            dataCache.clearCache(activeProject, 'books_detail');
            console.log(`🗑️ Cleared cache for ${activeProject} books to reload with media types`);
          }
          break;
        case 'companylist':
          setUploadStatus(`✅ Company list updated for ${activeProject}!`);
          // Clear cache to force reload of company data
          dataCache.clearCache(activeProject, 'companies');
          break;
      }

    } catch (error) {
      console.error('Upload error:', error);
      if (error.message.includes('unauthorized')) {
        setUploadStatus('❌ Upload failed: Authentication error. Please sign in again.');
      } else if (error.message.includes('permission')) {
        setUploadStatus('❌ Upload failed: Permission denied. Please check your Firebase Storage rules.');
      } else {
        setUploadStatus(`❌ Upload failed: ${error.message}`);
      }
    } finally {
      setUploading(false);
      const delay = uploadStatusRef.current.includes('Removed') ? 15000 : 5000;
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStatus('');
      }, delay);
    }
  };

  const handleBookCoverUpload = async (event) => {
    const files = event.target.files;
    if (files) {
      // Validate file types
      const validFiles = Array.from(files).filter(file => 
        file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg'
      );
      
      if (validFiles.length !== files.length) {
        setUploadStatus('❌ Some files are not PNG/JPG images. Only PNG/JPG files are allowed.');
        return;
      }
      
      try {
        setUploading(true);
        setUploadStatus('Converting images to WebP format...');
        
        // Convert to WebP
        const webpFiles = await batchConvertToWebP(validFiles, (current, total) => {
          const progress = Math.round((current / total) * 50); // First 50% for conversion
          setUploadProgress(progress);
          setUploadStatus(`Converting to WebP... ${current}/${total} (${progress}%)`);
        });
        
        setUploadStatus('Uploading WebP files...');
        
        // Upload WebP files
        await handleFileUpload(webpFiles, 'bookcovers');
        
      } catch (error) {
        console.error('Error processing book covers:', error);
        setUploadStatus(`❌ Error: ${error.message}`);
        setUploading(false);
      }
    }
  };

  const handleGLBUpload = (event, { skipCompression = false } = {}) => {
    const files = event.target.files;
    if (files) {
      const validFiles = Array.from(files).filter(file =>
        file.name.toLowerCase().endsWith('.glb')
      );

      if (validFiles.length !== files.length) {
        setUploadStatus('❌ Some files are not GLB files. Only .glb files are allowed.');
        return;
      }

      handleFileUpload(validFiles, 'glbmodels', { skipCompression });
    }
  };

  const handleExcelUpload = async (event, type) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        setUploadStatus('❌ Please upload an Excel file (.xlsx or .xls).');
        return;
      }

      if (type === 'booklist') {
        const existingNames = await firebaseService.listFileNames(`${activeProject.toUpperCase()}/books`);
        const existingCount = existingNames.filter(n => n.endsWith('.json') && n !== 'metadata.json').length;
        if (existingCount > 0) {
          const ok = window.confirm(
            `⚠️ ${activeProject.toUpperCase()} already has ${existingCount} book(s).\n\n` +
            `Uploading a new Excel will replace ALL existing book data and remove any books no longer in the new file.\n\n` +
            `Continue?`
          );
          if (!ok) {
            event.target.value = '';
            return;
          }
        }
      }

      handleFileUpload([file], type);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAuthenticated) {
      setUploadStatus('❌ Please sign in first to upload files.');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadStatus('❌ Please upload a PNG, JPG, or WebP image.');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(30);
      setUploadStatus('Converting logo to WebP...');

      const webpFile = await convertToWebP(file);

      setUploadProgress(60);
      setUploadStatus(`Uploading logo for ${activeProject}...`);

      await firebaseService.uploadProjectLogo(webpFile, activeProject);

      setUploadProgress(100);
      setUploadStatus(`✅ Logo updated for ${activeProject}!`);
    } catch (error) {
      console.error('Logo upload error:', error);
      setUploadStatus(`❌ Logo upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => { setUploadProgress(0); setUploadStatus(''); }, 5000);
    }
  };

  const handleUpdateMediaDetection = async () => {
    try {
      // Check authentication first
      if (!isAuthenticated) {
        setUploadStatus('❌ Please sign in first to update media detection.');
        return;
      }
      
      setUploading(true);
      setUploadStatus('Updating media detection for existing books...');
      setUploadProgress(0);
      
      let bookCodes = [];
      
      // Try to get book codes from metadata.json first
      const metadataPath = `${activeProject.toUpperCase()}/books/metadata.json`;
      const metadataUrl = await firebaseService.getFileUrl(metadataPath);
      
      if (metadataUrl) {
        // Use optimized metadata
        const response = await fetch(metadataUrl);
        const metadata = await response.json();
        bookCodes = metadata.books.map(book => book.code);
        console.log(`📊 Found ${bookCodes.length} books in metadata.json`);
      } else {
        // Fallback: Get book codes from Excel file
        const excelPath = `${activeProject.toUpperCase()}/data/booklist.xlsx`;
        const excelUrl = await firebaseService.getFileUrl(excelPath);
        
        if (!excelUrl) {
          setUploadStatus('❌ No book data found. Please upload Excel file first.');
          setUploading(false);
          return;
        }
        
        console.log('📊 Reading book codes from Excel file...');
        const response = await fetch(excelUrl);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        const headers = jsonData[0];
        let codeColumnIndex = headers.findIndex(header => 
          header && header.toLowerCase().includes('code')
        );
        
        if (codeColumnIndex === -1) {
          codeColumnIndex = headers.findIndex(header => 
            header && (
              header.toLowerCase().includes('book_code') ||
              header.toLowerCase().includes('bookcode') ||
              header.toLowerCase().includes('id') ||
              header.toLowerCase().includes('identifier')
            )
          );
        }
        
        // Extract book codes
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length > 0 && row[codeColumnIndex]) {
            const code = String(row[codeColumnIndex]).trim();
            if (/^[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+$/.test(code)) {
              bookCodes.push(code);
            }
          }
        }
        
        console.log(`📊 Found ${bookCodes.length} books in Excel file`);
      }
      
      if (bookCodes.length === 0) {
        setUploadStatus('❌ No valid book codes found.');
        setUploading(false);
        return;
      }
      
      console.log(`🔄 Updating media detection for ${bookCodes.length} books...`);
      
      // Update media detection
      console.log(`Starting media detection update for ${bookCodes.length} books`);
      await updateMediaDetectionForBooks(activeProject, bookCodes, (current, total) => {
        const progress = Math.round((current / total) * 100);
        console.log(`Progress update: ${current}/${total} (${progress}%)`);
        setUploadProgress(progress);
        setUploadStatus(`Updating media detection... ${current}/${total} (${progress}%)`);
      });
      
      // Clear cache to force reload
      dataCache.clearCache(activeProject, 'books_list');
      dataCache.clearCache(activeProject, 'books_detail');
      dataCache.clearCache(activeProject, 'books'); // Also clear legacy cache
      
      setUploadStatus(`✅ Successfully updated media detection for ${bookCodes.length} books!`);
      setUploadProgress(100);
      
    } catch (error) {
      console.error('Error updating media detection:', error);
      if (error.message.includes('unauthorized')) {
        setUploadStatus('❌ Error: Authentication error. Please sign in again.');
      } else if (error.message.includes('permission')) {
        setUploadStatus('❌ Error: Permission denied. Please check your Firebase Storage rules.');
      } else {
        setUploadStatus(`❌ Error: ${error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <nav className="bg-gray-900 shadow-lg border-b border-gray-700">
        <div className="container mx-auto px-4 py-3">
          {/* Mobile Navigation */}
          <div className="md:hidden">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-3">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-8 w-auto" />
            </div>
            
            {/* Title and Navigation Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-bold text-white">HK Pavilion CMS</h1>
              <div className="flex space-x-1">
                <a href="/" className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Book List</a>
                <a href="/companies" className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Companies</a>
                <a href="/admin" className="text-white font-semibold bg-red-600 px-2 py-1 rounded text-xs">Admin</a>
              </div>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-4">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-10 w-auto" />
            </div>
            
            {/* Title and Navigation Row */}
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-white">HK Pavilion CMS</h1>
              <div className="space-x-4">
                <a href="/" className="text-gray-300 hover:text-white">Book List</a>
                <a href="/companies" className="text-gray-300 hover:text-white">Companies</a>
                <a href="/admin" className="text-white font-semibold bg-red-600 px-3 py-1 rounded">Admin</a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="min-h-screen bg-gray-900">
        <div className="container mx-auto px-4 py-2 md:py-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-4 text-white">File Upload Management</h1>

            {/* Authentication Component */}
            <AuthComponent onAuthChange={handleAuthChange} />

            {/* Show admin content only when authenticated */}
            {isAuthenticated && (
              <>

          {/* Project Selection */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-white">Select Project</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map(project => (
                <div
                  key={project.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    activeProject === project.id
                      ? 'border-blue-500 bg-blue-900'
                      : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                  }`}
                  onClick={() => setActiveProject(project.id)}
                >
                  <h3 className="font-semibold text-white">{project.name}</h3>
                  <p className="text-sm text-gray-300">{project.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Type Selection */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-white">Upload Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {uploadTypes.map(type => (
                <div
                  key={type.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    uploadType === type.id
                      ? 'border-green-500 bg-green-900'
                      : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                  }`}
                  onClick={() => setUploadType(type.id)}
                >
                  <h3 className="font-semibold text-white">{type.name}</h3>
                  <p className="text-sm text-gray-300">{type.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Upload Interface */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-white">
              Upload {uploadTypes.find(t => t.id === uploadType)?.name}
            </h2>
            
            {uploadType === 'bookcovers' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 md:p-8 text-center">
                  <input
                    ref={bookCoverInputRef}
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg"
                    onChange={handleBookCoverUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => bookCoverInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                  >
                    Select PNG/JPG Images
                  </button>
                  <p className="text-sm text-gray-300 mt-2">
                    Select multiple PNG/JPG files. They will be converted to WebP format.
                  </p>
                </div>
              </div>
            )}

            {uploadType === 'glbmodels' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 md:p-8 text-center">
                  <input
                    ref={glbRawInputRef}
                    type="file"
                    multiple
                    accept=".glb"
                    onChange={(e) => handleGLBUpload(e, { skipCompression: true })}
                    className="hidden"
                  />
                  <input
                    ref={glbModelInputRef}
                    type="file"
                    multiple
                    accept=".glb"
                    onChange={(e) => handleGLBUpload(e)}
                    className="hidden"
                  />
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => glbRawInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                    >
                      Select GLB Files
                    </button>
                    <button
                      onClick={() => glbModelInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                    >
                      Select &amp; Compress GLB Files
                    </button>
                  </div>
                  <p className="text-sm text-gray-300 mt-3">
                    <strong>Select GLB Files</strong> — upload original files as-is.
                    <br />
                    <strong>Select &amp; Compress</strong> — auto Draco-compressed after upload (60-90% smaller, no quality loss).
                  </p>
                </div>
              </div>
            )}

            {uploadType === 'booklist' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 md:p-8 text-center">
                  <input
                    ref={bookListInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleExcelUpload(e, 'booklist')}
                    className="hidden"
                  />
                  <button
                    onClick={() => bookListInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                  >
                    Select Excel File
                  </button>
                  <p className="text-sm text-gray-300 mt-2">
                    Upload Excel file to update the book list.
                  </p>
                </div>
              </div>
            )}

            {uploadType === 'companylist' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 md:p-8 text-center">
                  <input
                    ref={companyListInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleExcelUpload(e, 'companylist')}
                    className="hidden"
                  />
                  <button
                    onClick={() => companyListInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                  >
                    Select Excel File
                  </button>
                  <p className="text-sm text-gray-300 mt-2">
                    Upload Excel file to update the company list.
                  </p>
                </div>
              </div>
            )}

            {uploadType === 'projectlogo' && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 md:p-8 text-center">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
                  >
                    Select Logo Image
                  </button>
                  <p className="text-sm text-gray-300 mt-2">
                    Upload a PNG/JPG/WebP image as the fair logo for <strong>{activeProject.toUpperCase()}</strong>. It will be converted to WebP.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Media Detection Update */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-white">
              Update Media Detection
            </h2>
            <p className="text-gray-300 mb-4">
              If you've uploaded book covers or 3D models after creating the book list, 
              use this to update the media detection for existing books.
            </p>
            <button
              onClick={handleUpdateMediaDetection}
              disabled={uploading}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg disabled:opacity-50"
            >
              {uploading ? 'Updating...' : 'Update Media Detection'}
            </button>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
              <h3 className="text-lg font-semibold mb-2 text-white">Upload Progress</h3>
              <div className="w-full bg-gray-700 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-300">{uploadProgress}% complete</p>
            </div>
          )}

          {/* Upload Status */}
          {uploadStatus && (
            <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
              <p className="text-sm text-white">{uploadStatus}</p>
            </div>
          )}

          {/* Manage Projects */}
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-white">Manage Projects</h2>
            <p className="text-gray-300 mb-4 text-sm">
              Add new book fair projects or change which project is the default. Changes take effect after page reload.
            </p>

            <div className="space-y-3 mb-4">
              {projects.map(project => (
                <div key={project.id} className="flex items-center justify-between bg-gray-700 rounded-lg p-4">
                  <div>
                    <span className="text-white font-semibold">{project.name}</span>
                    <span className="text-gray-400 text-sm ml-2">({project.id})</span>
                    {project.isDefault && (
                      <span className="ml-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded">Default</span>
                    )}
                    {project.description && (
                      <p className="text-gray-400 text-xs mt-1">{project.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {!project.isDefault && (
                      <button
                        onClick={() => handleSetDefault(project.id)}
                        disabled={projectSaving}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
                      >
                        Set Default
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {!showCreateProject ? (
              <button
                onClick={() => setShowCreateProject(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                + New Project
              </button>
            ) : (
              <div className="bg-gray-700 rounded-lg p-4 space-y-3">
                <h3 className="text-white font-semibold">Create New Project</h3>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Project ID (lowercase, no spaces)</label>
                  <input
                    type="text"
                    value={newProjectId}
                    onChange={(e) => setNewProjectId(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                    placeholder="e.g. bcbf2026"
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Display Name</label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="e.g. BCBF 2026"
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newProjectDesc}
                    onChange={(e) => setNewProjectDesc(e.target.value)}
                    placeholder="e.g. Bologna Children's Book Fair 2026"
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateProject}
                    disabled={projectSaving}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
                  >
                    {projectSaving ? 'Creating...' : 'Create Project'}
                  </button>
                  <button
                    onClick={() => { setShowCreateProject(false); setProjectStatus(''); }}
                    className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {projectStatus && (
              <p className="text-sm text-yellow-300 mt-3">{projectStatus}</p>
            )}
          </div>

          {/* Current Project Info */}
                <div className="bg-gray-800 rounded-lg p-6 mt-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-2 text-white">Current Project: {projects.find(p => p.id === activeProject)?.name}</h3>
                  <p className="text-sm text-gray-300">
                    All uploads will be associated with this project. Switch projects to manage different book fair events.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPanel;
